import * as React from 'react';
import { useContext, useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import { Button, TextField } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AuthContext } from '../store/authContext';

const useStyles = makeStyles((theme: Theme) => ({
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
    textAlign: 'center',
  },
  button: {
    marginTop: theme.spacing(4),
    width: '80%',
    display: 'flex',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
}));

type FormInputs = {
  username: string;
  password: string;
};

const LoginForm: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('auth');
  const { login, isLoggedIn } = useContext(AuthContext);
  const history = useNavigate();

  const { register, handleSubmit, formState: { errors } } = useForm<FormInputs>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const onSubmit = async (data: FormInputs) => {
    setLoading(true);
    await login(data.username, data.password);
    setLoading(false);
  };

  useEffect(() => {
    if (isLoggedIn && !loading) {
      history('/');
    }
  }, [history, isLoggedIn, loading]);

  const requiredMessage = t('validation:required');
  return (
    <form style={{ width: '100%',
            marginTop: 8,
            textAlign: 'center',
          }}
          onSubmit={handleSubmit(onSubmit)}>
      <TextField
        {...register('username', {
          required: requiredMessage,
        })}
        variant="outlined"
        color="secondary"
        margin="normal"
        fullWidth
        id="username"
        label={t('username')}
        autoComplete="username"
        autoFocus
        error={!!errors.username}
        helperText={errors.username?.message}
      />
      <TextField
        {...register('password', { required: requiredMessage })}
        variant="outlined"
        color="secondary"
        margin="normal"
        fullWidth
        label={t('password')}
        type="password"
        id="password"
        autoComplete="current-password"
        error={!!errors.password}
        helperText={errors.password?.message}
      />
      <Button
        type="submit"
        variant="contained"
        color="primary"
        sx={{
          marginTop: 24,
          width: '80%',
          display: 'flex',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
        disabled={loading}
      >
        {t('login')}
      </Button>
    </form>
  );
};

export default LoginForm;
